import * as React from 'react'
import { Redirect } from 'react-router-dom'
import styled from 'styled-components'
import { compose, layout, LayoutProps } from 'styled-system'
import { FormikHelpers } from 'formik'

import {
  Box,
  Button,
  RadioButton,
  RadioGroup,
  TextBody,
  upToMd,
} from '@ally/metronome-ui'
import { Form, Forms, SSN } from '@ally/forms'
import { RecoveryOptionsProps } from '@ally/transmitigator'
import { FormField, LoginHeading } from '@ally/ally-login'

import { SSNModal } from '../../Modals'
import { useTransmit } from '../../TransmitContext'
import { CIAMErrorBanner } from '../Shared'

const StyledForm = styled(Form)<LayoutProps>`
  ${compose(layout)};
`

const StyledFieldWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
`

const FORM_ID = 'ally-login-forgot-password-v2-recovery-options-step-2-form'

type RecoveryOption = 'passwordHint' | 'createNewPassword'

interface RecoveryOptionsStepForm {
  ssn?: string
  recoveryOption: RecoveryOption
}

const initialValues: RecoveryOptionsStepForm = {
  ssn: '',
  recoveryOption: 'passwordHint',
}

const PasswordHintLabel: React.FC = () => (
  <TextBody size="sm" tag="span">
    Get a password hint by email or text (based on your delivery preference)
  </TextBody>
)

const CreateNewPasswordLabel: React.FC<{ selection: RecoveryOption }> = ({
  selection,
}) => (
  <TextBody size="sm" tag="span">
    {selection === 'passwordHint'
      ? 'Create a new password'
      : "Create a new password (we'll send you a security code first)"}
  </TextBody>
)

const RecoveryOptionStepContent: React.FC<RecoveryOptionsProps> = ({
  error,
  isCustomer,
  isResetPassword,
  status,
  errorCode = '',
  onComplete,
}) => {
  const { hostServices } = useTransmit()
  const [recoveryOptionSelection, setRecoveryOptionSelection] = React.useState<
    RecoveryOption
  >(isResetPassword ? 'createNewPassword' : 'passwordHint')

  React.useEffect(() => {
    hostServices?.analytics.trackPageView({
      eventType: 'pageview',
      pageName: 'AOS:Login:Forgot Password:Recovery Option Step',
    })
  }, [])

  if (error && error === 'CIAM_E08_00_004') {
    return <Redirect to="/security/error" />
  }

  const onSubmit = (
    form: RecoveryOptionsStepForm,
    { setSubmitting }: FormikHelpers<RecoveryOptionsStepForm>,
  ): void => {
    form.recoveryOption = recoveryOptionSelection
    onComplete({
      ssn: form.ssn,
      passwordHint: form.recoveryOption === 'passwordHint',
      createNewPassword: form.recoveryOption === 'createNewPassword',
      isResetPassword,
    })
    // NOTE: this allows for form resubmission after failure
    setSubmitting(false)
  }

  const handleChange = (selected: string): void => {
    setRecoveryOptionSelection(selected as RecoveryOption)
  }

  const title = isResetPassword ? 'Reset Password' : 'Forgot Password'

  return (
    <>
      <LoginHeading title={title} />
      {isCustomer && (
        <Box mb="md" display="flex" width={upToMd<any, any>(12, 6) as any}>
          <TextBody tag="p" size="md">
            To verify that it&apos;s you, enter your full Social Security
            number.
          </TextBody>
        </Box>
      )}

      <CIAMErrorBanner error={errorCode as string} mb="md" />

      <Box display="flex">
        <Forms
          formId={FORM_ID}
          initialValues={initialValues}
          onSubmit={onSubmit}
        >
          <StyledForm id={FORM_ID} autoComplete="off" width={12}>
            <FormField>
              {isCustomer && (
                <>
                  <Box mb="md" width={upToMd(12, 7)}>
                    <SSN
                      label="Social security number"
                      name="ssn"
                      data-allytmfn="ssn"
                      required
                      data-testid="forgot-password-v2-ssn-input"
                    />
                  </Box>
                  <Box mb="md">
                    <SSNModal
                      withAllyTM="forgotPassword-whyWeAskForYourSSN"
                      textBodyType="forgotPassword"
                    />
                  </Box>
                </>
              )}

              {!isResetPassword && (
                <StyledFieldWrapper>
                  <Box
                    mb="md"
                    display="flex"
                    width={upToMd<any, any>(12, 6) as any}
                  >
                    <TextBody tag="p" size="sm" weight="bold">
                      Password recovery options
                    </TextBody>
                  </Box>

                  <RadioGroup
                    legendText="Password recovery options"
                    isLegendHidden
                    name="recoveryOption"
                    data-allytmfn="recoveryOption"
                    defaultValue="passwordHint"
                    onChange={handleChange}
                  >
                    <RadioButton
                      label={<PasswordHintLabel />}
                      value="passwordHint"
                    />
                    <RadioButton
                      label={
                        <CreateNewPasswordLabel
                          selection={recoveryOptionSelection}
                        />
                      }
                      value="createNewPassword"
                    />
                  </RadioGroup>
                </StyledFieldWrapper>
              )}

              <Button
                allytmln="forgotPassword-SSN-continue"
                type="submit"
                variant="primary"
                text="Continue"
                loading={status === 'Loading'}
              />
            </FormField>
          </StyledForm>
        </Forms>
      </Box>
    </>
  )
}

export const RecoveryOptionStep: React.FC = () => {
  const { activeScreen } = useTransmit()

  if (activeScreen.type !== 'RecoveryOptions') {
    return null
  }

  // eslint-disable-next-line react/jsx-props-no-spreading
  return <RecoveryOptionStepContent {...activeScreen.payload} />
}
